import { ElementPropertyName } from '@shared/models/element-property.model';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { HEAT_DEMAND_RECORD_NAME } from '@shared/templates/categories/heating/heating.model';
import { getBuildingEnvelopeElements } from '../../energy/building-envelope.helpers';
import { getElementCategoryId } from '../../element-category.helpers';
import { projectGuards } from '../../guards/project-guards.helpers';
import { getEnvelopeUValueNodeIds } from '../envelope-u-value-nodes.helpers';
import { GLOBAL_VAR_PREFIX, NODE_ID } from '../node-ids.helpers';
import type { ComputedVariable } from './types';
import { getBuildingHeatingDemand } from '../../energy/energy.helpers';
import { getProjectMeta } from '../../project.helpers';
import { getBuildingDesignFromProjectOrBuilding } from '../../design/design.helpers';
import {
  getEnergyActivitiesFromDesign,
  getHeatedAreaFromEnergyActivities,
} from '../../energy/energy-activity.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  applyProjectChangesToParent,
  createPropertyResolvedChange,
} from '../update-engine.helpers';

const heatDemandRecordNodeId = `${GLOBAL_VAR_PREFIX}${HEAT_DEMAND_RECORD_NAME}`;
/** Exported so AHU variables computed variable can depend on it */
export const postHeatingDemandNodeId = `${GLOBAL_VAR_PREFIX}post_heating_demand`;
const uValuePropName = ElementPropertyName.ThermalTransmittance;

type PropertyDescriptor = {
  type: string;
  elementId: string;
  name: string;
};

/** Build version with envelope u-values set from resolved deps, then compute heat demand record (e.g. postHeatingDemand). */
const computePostHeatingDemandFromResolved = (
  version: import('@shared/models/project.model').Baseline,
  resolvedDeps: Record<string, unknown>,
  descriptorMap: Map<string, unknown>,
  envelopeNodeIds: string[],
  project: import('@shared/models/project.model').Project | undefined,
  weatherHours:
    import('../../energy/weather.models').EnvironmentHourData[] | undefined,
  proposal?: import('@shared/models/proposals.model').IProposal,
): number => {
  if (!project) return 0;
  if (!weatherHours || weatherHours.length !== 8760) return 0;

  const resolvedChanges = envelopeNodeIds
    .map((nodeId) => {
      const desc = descriptorMap.get(nodeId) as PropertyDescriptor | undefined;
      const value = resolvedDeps[nodeId];
      if (!desc || desc.type !== 'property' || !guards.is.number(value))
        return null;
      return createPropertyResolvedChange(desc.elementId, desc.name, value);
    })
    .filter((u): u is NonNullable<typeof u> => u !== null);

  const versionWithResolved =
    resolvedChanges.length > 0
      ? applyProjectChangesToParent(version, resolvedChanges)
      : version;

  const design = getBuildingDesignFromProjectOrBuilding(project);
  const activities = design ? getEnergyActivitiesFromDesign(design) : [];
  const heatedArea = getHeatedAreaFromEnergyActivities(activities);

  const demand = getBuildingHeatingDemand(versionWithResolved, {
    metadata: design ? undefined : getProjectMeta(project),
    environmentHours: weatherHours,
    heatedArea,
    design,
    proposal,
  });

  return typeof demand.postHeatingDemand === 'number'
    ? demand.postHeatingDemand
    : 0;
};

export const heatDemandRecordVariable: ComputedVariable = {
  name: HEAT_DEMAND_RECORD_NAME,
  getDependencies: (ctx) => {
    const envelopeIds = getEnvelopeUValueNodeIds(
      ctx.version,
      ctx.elementIdsInScope,
      ctx.proposal,
    );
    // Nodes that depend on heat_demand_record get envelope + this node so they resolve after we have postHeatingDemand
    return [...envelopeIds, postHeatingDemandNodeId];
  },
  ensureNodes: (ctx) => {
    const envelopeUValueNodeIds = getEnvelopeUValueNodeIds(
      ctx.version,
      ctx.elementIdsInScope,
      ctx.proposal,
    );
    for (const envelopeElement of getBuildingEnvelopeElements(
      ctx.version,
      ctx.proposal,
    )) {
      if (!ctx.elementIdsInScope.has(envelopeElement.id)) continue;
      const nodeId = NODE_ID.property(envelopeElement.id, uValuePropName);
      if (ctx.descriptorMap.has(nodeId)) continue;
      ctx.descriptorMap.set(nodeId, {
        type: 'property',
        elementId: envelopeElement.id,
        name: uValuePropName,
      });
      ctx.nodes.push({ id: nodeId, dependencies: [], compute: () => 0 });
    }

    // Post-heating demand node: computed after envelope u-values are resolved
    if (!ctx.descriptorMap.has(postHeatingDemandNodeId)) {
      ctx.descriptorMap.set(postHeatingDemandNodeId, {
        type: 'global',
        elementId: '',
        name: 'post_heating_demand',
      });
      const version = ctx.version;
      const descriptorMap = ctx.descriptorMap;
      const project = ctx.project;
      const weatherHours = ctx.weatherHours;
      const proposal = ctx.proposal;
      ctx.nodes.push({
        id: postHeatingDemandNodeId,
        dependencies: [...envelopeUValueNodeIds],
        compute: (resolvedDeps) =>
          computePostHeatingDemandFromResolved(
            version,
            resolvedDeps as Record<string, unknown>,
            descriptorMap,
            envelopeUValueNodeIds,
            project,
            weatherHours,
            proposal,
          ),
      });
    }

    // Heating elements depend on all envelope u-values (heating demand uses transmission)
    for (const element of ctx.elementsInScope) {
      if (!projectGuards.is.element(element)) continue;
      if (getElementCategoryId(element) !== ElementCategoryID.Heating) continue;
      const resultsNodeId = NODE_ID.results(element.id);
      if (ctx.descriptorMap.has(resultsNodeId)) continue;
      ctx.nodes.push({
        id: resultsNodeId,
        dependencies: [...envelopeUValueNodeIds],
        compute: () => 0,
      });
    }
  },
  resolve: (resolvedDeps) =>
    (resolvedDeps[heatDemandRecordNodeId] as number) ??
    (resolvedDeps[postHeatingDemandNodeId] as number) ??
    0,
};
